// xG Timeline — D3 SVG area/line chart (Phase 3, the last renderer).
//
// One team's npG − npxG over recent games (per-game or 5-game rolling average),
// as an area+line split at the zero line: overperformance (blue) clipped above,
// underperformance (red) clipped below. React owns the container + the <svg>
// skeleton (defs = per-team clipPaths + gradients, the grid group, the two area
// paths, the line, the dots group); D3 owns the geometry in a useEffect (a port
// of the legacy draw). The area/line/dots animate via D3 transitions, so — like
// Performance Trajectory — the first draw for a team snaps and later draws
// animate. The clip/gradient ids are per-team, so a *team* change is a fresh
// draw (snap), matching the legacy `body.dataset.xgtTeam` rebuild; a
// timeline/view change keeps the skeleton and animates. ResizeObserver redraws
// (no transition) on resize, cleaned up in the effect return. Fixture + timeline
// + team + view from the context.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { scaleLinear as d3ScaleLinear } from "d3-scale";
import {
  area as d3Area,
  line as d3Line,
  curveCatmullRom as d3CurveCatmullRom,
} from "d3-shape";
import { ticks } from "d3-array";
import { easeCubicOut } from "d3-ease";
import { selectXgTimeline, xgtTooltip, type XgTimelineShape } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

// The scale/area/line generators are typed with minimal locals matching
// exactly how this file calls them (see HalfDonut for the same pattern).
interface LinearScale {
  (v: number): number;
  domain(d: [number, number]): LinearScale;
  range(r: [number, number]): LinearScale;
}
interface AreaGen {
  (data: number[]): string | null;
  x(fn: (d: number, i: number) => number): AreaGen;
  y0(v: number): AreaGen;
  y1(fn: (d: number) => number): AreaGen;
  curve(c: unknown): AreaGen;
}
interface LineGen {
  (data: number[]): string | null;
  x(fn: (d: number, i: number) => number): LineGen;
  y(fn: (d: number) => number): LineGen;
  curve(c: unknown): LineGen;
}
interface CatmullRom {
  alpha(a: number): unknown;
}
const scaleLinear = d3ScaleLinear as unknown as () => LinearScale;
const area = d3Area as unknown as () => AreaGen;
const line = d3Line as unknown as () => LineGen;
const curveCatmullRom = d3CurveCatmullRom as unknown as CatmullRom;

function draw(
  svgEl: SVGSVGElement,
  wrap: HTMLElement,
  shape: XgTimelineShape,
  teamKey: string,
  useTransition: boolean
): void {
  const cW = wrap.clientWidth;
  const cH = wrap.clientHeight;
  if (!cW || !cH) return; // not laid out yet — the ResizeObserver retries

  const { displayData } = shape;
  const areaSc = Math.sqrt((cW * cH) / (250 * 100));
  const dotR = Math.max(1.875, Math.min(6.25, 2.75 * areaSc));
  const ML = 28,
    MR = Math.ceil(dotR) + 2,
    MT = Math.ceil(dotR) + 2,
    MB = 16;
  const n = displayData.length;

  const xScale = scaleLinear().domain([1, n]).range([ML, cW - MR]);
  const maxAbs = Math.max(1.5, ...displayData.map((d) => Math.abs(d)));
  const yScale = scaleLinear().domain([-maxAbs, maxAbs]).range([cH - MB, MT]);
  const y0 = yScale(0);
  const curve = curveCatmullRom.alpha(0.5);
  const DUR = useTransition ? 450 : 0;
  const EASE = easeCubicOut;

  const svg = select(svgEl).attr("width", cW).attr("height", cH);

  // Clip rects + gradient stop coords (instant — structural).
  svg
    .select(".xgt-cr-above")
    .attr("x", ML)
    .attr("y", MT)
    .attr("width", cW - ML - MR)
    .attr("height", Math.max(0, y0 - MT));
  svg
    .select(".xgt-cr-below")
    .attr("x", ML)
    .attr("y", y0)
    .attr("width", cW - ML - MR)
    .attr("height", Math.max(0, cH - MB - y0));
  svg.select(`#xgt-grad-above-${teamKey}`).attr("x1", 0).attr("y1", MT).attr("x2", 0).attr("y2", y0);
  svg.select(`#xgt-grad-below-${teamKey}`).attr("x1", 0).attr("y1", y0).attr("x2", 0).attr("y2", cH - MB);

  // Grid (instant — cleared + redrawn each draw).
  const grid = svg.select<SVGGElement>(".xgt-grid");
  grid.selectAll("*").remove();
  ticks(-maxAbs, maxAbs, 5).forEach((v) => {
    const ty = yScale(v);
    const isZero = v === 0;
    grid
      .append("line")
      .attr("x1", ML)
      .attr("x2", cW - MR)
      .attr("y1", ty)
      .attr("y2", ty)
      .attr("stroke", isZero ? "#555" : "#383838")
      .attr("stroke-dasharray", "4,4")
      .attr("stroke-width", isZero ? 1 : 0.5);
    grid
      .append("text")
      .attr("x", ML - 4)
      .attr("y", ty + 3)
      .attr("text-anchor", "end")
      .attr("fill", "rgba(255,255,255,0.45)")
      .style("font-size", "var(--t-label)")
      .attr("font-family", "inherit")
      .text(v.toFixed(1));
  });
  ticks(1, n, Math.min(n, 5)).forEach((i) => {
    grid
      .append("text")
      .attr("x", xScale(i))
      .attr("y", cH - 3)
      .attr("text-anchor", "middle")
      .attr("fill", "rgba(255,255,255,0.4)")
      .style("font-size", "var(--t-label)")
      .attr("font-family", "inherit")
      .text(i);
    grid
      .append("line")
      .attr("x1", xScale(i))
      .attr("x2", xScale(i))
      .attr("y1", MT)
      .attr("y2", cH - MB)
      .attr("stroke", "#383838")
      .attr("stroke-dasharray", "4,4")
      .attr("stroke-width", 0.5);
  });

  // Area paths + line — transition the `d` attribute (current → new).
  const areaPath = area()
    .x((_, i) => xScale(i + 1))
    .y0(y0)
    .y1((d) => yScale(d))
    .curve(curve);
  const linePath = line()
    .x((_, i) => xScale(i + 1))
    .y((d) => yScale(d))
    .curve(curve);

  const areaD = areaPath(displayData) ?? "";
  svg.select(".xgt-area-above").transition().duration(DUR).ease(EASE).attr("d", areaD);
  svg.select(".xgt-area-below").transition().duration(DUR).ease(EASE).attr("d", areaD);
  svg
    .select(".xgt-line")
    .transition()
    .duration(DUR)
    .ease(EASE)
    .attr("d", linePath(displayData) ?? "");

  // Dots — enter/update/exit keyed by index; transition cx/cy from current.
  const dotsG = svg.select<SVGGElement>(".xgt-dots");
  const dots = dotsG
    .selectAll<SVGCircleElement, number>("circle")
    .data(displayData, (_, i) => String(i));
  dots
    .enter()
    .append("circle")
    .attr("class", "chart-dot")
    .attr("r", dotR)
    .attr("fill", "#fff")
    .attr("cx", (_, i) => xScale(i + 1))
    .attr("cy", y0)
    .style("cursor", "default");
  dotsG
    .selectAll<SVGCircleElement, number>("circle")
    .attr("data-tooltip", (_, i) => xgtTooltip(shape, i))
    .transition()
    .duration(DUR)
    .ease(EASE)
    .attr("r", dotR)
    .attr("cx", (_, i) => xScale(i + 1))
    .attr("cy", (d) => yScale(d));
  dots.exit().remove();
}

export interface XgTimelineProps {
  fixture: string;
  timeline?: string | null;
  team?: string | null;
  view?: string | null;
}

export default function XgTimeline({
  fixture,
  timeline,
  team,
  view,
}: XgTimelineProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const prevTeamRef = useRef<string | null>(null);

  const { data, isPending, isError, refetch } = useWidgetData(
    "xg-timeline",
    useFixtureKey(fixture)
  );

  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const homeTeam = (parts[0] ?? "").trim();
  const chosen = team || homeTeam;
  const teamKey = chosen.replace(/[^a-zA-Z0-9]/g, "-");
  const shape = selectXgTimeline(data ?? {}, chosen, timeline, view);
  const sig = `${teamKey}|${JSON.stringify(shape)}`;

  useEffect(() => {
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !shape) return;
    // A team change re-keys the clip/gradient ids (legacy rebuilds the HTML), so
    // it's a fresh draw that snaps; a timeline/view change keeps the skeleton and
    // animates. Mirrors the legacy `draw(!isNewHtml)`.
    const isNewHtml = prevTeamRef.current !== teamKey;
    prevTeamRef.current = teamKey;
    draw(svgEl, wrap, shape, teamKey, !isNewHtml);

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => draw(svgEl, wrap, shape, teamKey, false));
    ro.observe(wrap);
    return () => ro.disconnect();
    // `sig` captures team + the plotted series (the complete draw input).
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!shape) {
    return (
      <div className="data-wrapper top-align">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  return (
    <div className="data-wrapper xgt-wrapper" ref={wrapRef}>
      <svg className="xgt-svg" ref={svgRef}>
        <defs>
          <clipPath id={`xgt-above-${teamKey}`}>
            <rect className="xgt-cr-above" />
          </clipPath>
          <clipPath id={`xgt-below-${teamKey}`}>
            <rect className="xgt-cr-below" />
          </clipPath>
          <linearGradient id={`xgt-grad-above-${teamKey}`} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#2d56b4" stopOpacity="1" />
            <stop offset="100%" stopColor="#2d56b4" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`xgt-grad-below-${teamKey}`} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#7f1d1d" stopOpacity="0" />
            <stop offset="100%" stopColor="#7f1d1d" stopOpacity="1" />
          </linearGradient>
        </defs>
        <g className="xgt-grid" />
        <path
          className="xgt-area-above bento-fill"
          fill={`url(#xgt-grad-above-${teamKey})`}
          clipPath={`url(#xgt-above-${teamKey})`}
        />
        <path
          className="xgt-area-below bento-fill"
          fill={`url(#xgt-grad-below-${teamKey})`}
          clipPath={`url(#xgt-below-${teamKey})`}
        />
        <path className="xgt-line" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
        <g className="xgt-dots" />
      </svg>
    </div>
  );
}
